// AUTO-GENERATED — do not edit. Source: /shared/. Run scripts/sync-shared.sh to update.
/**
 * SLIK — NO-SHOW BLOCK brain (Jose 2026-08-07)
 * ============================================
 * A client who no-shows TWICE BACK-TO-BACK (no call, no cancel) is auto-blocked from normal
 * scheduling: they must PAY UPFRONT (reuses the existing paymentSettings.requirePaymentUpfront)
 * and sign a short agreement to keep booking. They earn the block back OFF by completing THREE
 * services while blocked (3 completed > 2 no-shows) — a single completed visit does NOT lift it.
 * The owner can also flip it off/on manually (the master switch in the client profile).
 *
 * Pure logic + types only — NO React, NO Firebase. Each app feeds in the client's attendance
 * history. Edit THIS file at repo root and run scripts/sync-shared.sh. Do NOT edit per-app copies.
 */

/** Consecutive no-shows that trigger the block. */
export const NO_SHOW_BLOCK_THRESHOLD = 2;

/** Completed services (while blocked) that auto-lift the block — must exceed the no-shows. */
export const REDEMPTION_SERVICES = 3;

/**
 * LATE-CANCEL = NO-SHOW (Jose 2026-08-07). A cancellation within this many minutes of the appointment
 * counts the SAME as a no-show (a strike toward the block); a cancellation with MORE notice is
 * "enough notice" and never counts. Make it a shop setting later; default 60 min.
 */
export const LATE_CANCEL_WINDOW_MIN = 60;

/**
 * Is a cancellation "late" → counts as a no-show strike?
 *
 * LATE = cancelling an UPCOMING appointment within `windowMin` minutes BEFORE it starts
 * (0 ≤ start − cancel ≤ window). More notice than that = "enough notice", never counts.
 *
 * FIX (Jose 2026-09-06): the old check was `(start − cancel) ≤ window`, which is TRUE for any
 * appointment already in the PAST (start − now is negative → always ≤ window). That made every
 * past/stale appointment falsely warn "within 1 hour of the start" on cancel. A late-cancel is only
 * about failing to give notice for an UPCOMING slot; once the start time has passed, a missed
 * appointment is a NO-SHOW (use the No-show action), not a late cancellation. Requiring the lower
 * bound (start ≥ cancel) scopes the penalty to genuine within-the-window-before cancels.
 * `windowMin` is per-shop (BookingRules.lateCancelWindowMin); defaults to LATE_CANCEL_WINDOW_MIN.
 */
export function isLateCancel(
  apptStartMs: number,
  cancelAtMs: number,
  windowMin: number = LATE_CANCEL_WINDOW_MIN,
): boolean {
  if (!Number.isFinite(apptStartMs) || !Number.isFinite(cancelAtMs)) return false;
  const win = (typeof windowMin === 'number' && windowMin >= 0) ? windowMin : LATE_CANCEL_WINDOW_MIN;
  const untilStartMs = apptStartMs - cancelAtMs;
  return untilStartMs >= 0 && untilStartMs <= win * 60000;
}

const ATTENDED = 'completed';
const MISSED = 'no-show';

export interface AttendanceRecord {
  status: string;   // appointment status
  dateMs: number;   // appointment date+time as epoch ms (for ordering)
}

/** Attendance outcomes (completed / no-show only — cancellations are neutral), most-recent first. */
function outcomes(records: AttendanceRecord[]): string[] {
  return records
    .filter((r) => r.status === ATTENDED || r.status === MISSED)
    .sort((a, b) => b.dateMs - a.dateMs)
    .map((r) => r.status);
}

/**
 * Should this client be blocked? True when the `threshold` most recent attendance outcomes are ALL
 * no-show (back-to-back). A completed visit between no-shows breaks the streak; a cancellation
 * doesn't count either way. `threshold` is per-shop (BookingRules.noShowBlockThreshold); defaults
 * to NO_SHOW_BLOCK_THRESHOLD.
 */
export function hitsNoShowBlock(
  records: AttendanceRecord[],
  threshold: number = NO_SHOW_BLOCK_THRESHOLD,
): boolean {
  const n = (typeof threshold === 'number' && threshold >= 1) ? Math.floor(threshold) : NO_SHOW_BLOCK_THRESHOLD;
  const recent = outcomes(records).slice(0, n);
  return recent.length >= n && recent.every((s) => s === MISSED);
}

/** How many services the client has completed SINCE the block was applied (redemption progress). */
export function completedSinceBlock(records: AttendanceRecord[], blockedAtMs: number): number {
  return records.filter((r) => r.status === ATTENDED && r.dateMs > blockedAtMs).length;
}

/** Enough completed services since the block (3+) to auto-lift it. */
export function shouldAutoLift(records: AttendanceRecord[], blockedAtMs: number): boolean {
  return completedSinceBlock(records, blockedAtMs) >= REDEMPTION_SERVICES;
}

/** The default client-facing notice + agreement copy (Jose's wording, tightened). */
export const DEFAULT_NO_SHOW_NOTICE =
  "You've been denied online scheduling because of two back-to-back no-shows. " +
  'To continue as a client and receive services, your appointment must be paid in full upfront. ' +
  'If you agree, check the box below — this acts as your electronic signature authorizing prepayment.';
